import React, {useEffect, useState} from 'react';
import axios, {AxiosError} from 'axios';

import {BarLoader} from 'react-spinners';

import FreightTable from './components/FreightTable'
import StatusOverview from "./components/StatusOverview";
import DriverAndVehicle from "./components/DriverAndVehicle";
import ConsignmentWaypointAddress from "./components/ConsignmentWaypointAddress";
import Map from "./components/Map";
import ServiceRating from "./components/ServiceRating";
import DriverMessage from "./components/DriverMessage";

import FreightList from "./components/FreightList";
import SubTruxBrand from "./components/SubTruxBrand";
import Timeline from "./components/Timeline";
import {IconReceiver, IconSender} from "./components/Icons";

// Simple flexbox based responsive grid layout
// http://flexboxgrid.com/
// https://github.com/kristoferjoseph/flexboxgrid
import 'flexboxgrid/dist/flexboxgrid.css'
import './App.scss';
import CallToAction from "./components/CallToAction";
import {ConsignmentStatus, PublicApiConsignmentResponse} from "./Representation";
import API from "./API";
import ConsignmentImageGallery from "./components/ConsignmentImageGallery";
import {useInterval} from "usehooks-ts";
import BtnRefresh from "./components/BtnRefresh";


type CriticalErrorType = 'MISSING_TOKEN' | 'INVALID_TOKEN' ;


const App = () => {

    const [ stateConsignment, setStateConsignment ] = useState<PublicApiConsignmentResponse | null>(null);
    const [ stateLoading, setStateLoading ] = useState(true);
    const [ stateRefreshing, setStateRefreshing ] = useState(false);
    const [ stateFreightAsTable, setStateFreightAsTable ] = useState<boolean>(false);
    const [ stateCircularDriverPhoto, setStateCircularDriverPhoto ] = useState<boolean>(false);
    const [ stateCriticalErrorType, setStateCriticalErrorType ] = useState<CriticalErrorType | null>(null);
    const [ stateRetryableError, setStateRetryableError ] = useState<AxiosError | null>(null);
    const [ stateTrackingToken, setStateTrackingToken ] = useState<string | null>(null);
    const [ stateAutoRefreshEnabled, setStateAutoRefreshEnabled ] = useState(false);

    const shouldAutoRefreshConsignment = (consignment: PublicApiConsignmentResponse) => {
        return ConsignmentStatus.COMPLETED !== consignment.status
            && ConsignmentStatus.CANCELLED !== consignment.status
    }
    const fetchConsignment = (trackingToken: string) => {
        const cacheBuster = new Date().getTime(); // Fucking IE again!
        setStateRefreshing(true)
        axios.get<PublicApiConsignmentResponse>(`/tracking/${trackingToken}?cacheBuster=${cacheBuster}` )
            .then(response => {
                const consignment = response.data;
                setStateRetryableError(null)
                setStateCriticalErrorType(null)
                setStateConsignment(consignment)
            })
            .catch((e: AxiosError) => {
                if (e.response?.status === 403) {
                    setStateAutoRefreshEnabled(false)
                    setStateCriticalErrorType('INVALID_TOKEN')
                } else {
                    setStateRetryableError(e)
                }
            })
            .finally(() => {
                setStateLoading(false)
                setStateRefreshing(false)
            });
    };

    const refresh = () => {
        if (stateTrackingToken) {
            setStateRefreshing(true);
            fetchConsignment(stateTrackingToken);
        }
    };

    useInterval(() => {
        refresh()
    }, stateAutoRefreshEnabled && stateTrackingToken ? 120_000 : null);

    useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search);
        const trackingToken = searchParams.get('t');
        // console.log("trackingToken", trackingToken);
        setStateFreightAsTable(!!searchParams.get('table'))
        setStateCircularDriverPhoto(!!searchParams.get('circularDriverPhoto'))
        if (!trackingToken) {
            setStateCriticalErrorType('MISSING_TOKEN')
            setStateLoading(false)
        } else {
            setStateTrackingToken(trackingToken)
            setStateAutoRefreshEnabled(true)
            fetchConsignment(trackingToken);
        }
    }, [])

    useEffect(() => {
        if (stateAutoRefreshEnabled && stateConsignment?.status) {
            setStateAutoRefreshEnabled(shouldAutoRefreshConsignment(stateConsignment))
        }
    }, [stateAutoRefreshEnabled, stateConsignment])

    const consignment = stateConsignment;
    const receiverAddress = consignment && consignment.receiver && consignment.receiver.address;
    const apiOrigin = API.origin();
    const driverLatLng = consignment?.driver && consignment.driver.latitude && consignment.driver.longitude
        ? {lat: consignment.driver.latitude, lng: consignment.driver.longitude}
        : null;
    const driverBearing = consignment?.driver?.bearing ?? 0;

    // console.log(consignment)

    return (
        <div className="app">

            <div style={{'minHeight': '8px'}}>
                <BarLoader
                    width={'100%'}
                    height={3}
                    color={'#434343'}
                    loading={stateLoading || stateRefreshing}
                />
            </div>

            <div className="content">

            {stateLoading || stateCriticalErrorType || !stateTrackingToken || !consignment
                ?
                <div className="header">
                    <h1 className="center-text">Tracking</h1>
                    {stateCriticalErrorType === 'MISSING_TOKEN' &&
                        <p className="center-text">The link you have clicked is invalid</p>
                    }
                    {stateCriticalErrorType === 'INVALID_TOKEN' &&
                        <p className="center-text">Invalid or expired tracking link</p>
                    }
                    {!stateCriticalErrorType && !consignment && stateRetryableError &&
                        <>
                            <p className="center-text">
                                An error occurred, please try again<br />
                                {stateRetryableError?.message}
                            </p>
                            <p className="center-text">
                                <BtnRefresh refresh={refresh} />
                            </p>
                        </>
                    }
                </div>
                :
                <div>
                    {stateRetryableError &&
                        <div className="error center-text">
                            An error occurred, please try again<br />
                            {stateRetryableError?.message}
                        </div>
                    }
                    <div className="clearfix header">
                        <img className="logo" src={`${apiOrigin}/tracking/${stateTrackingToken}/logo`} alt="Sender logo" />
                        <div className="actions">
                            {/*{(stateAutoRefreshEnabled && !stateRetryableError) && <p><IconCheck />&nbsp;Live Updates</p>}*/}
                            <BtnRefresh refresh={refresh} />
                        </div>
                    </div>

                    <div className="clearfix">
                        <h1>{consignment?.reference}</h1>
                    </div>

                    <div className="clearfix">
                        <StatusOverview
                            apiOrigin={apiOrigin}
                            trackingToken={stateTrackingToken}
                            consignment={consignment}
                        />
                    </div>

                    <div className="clearfix">
                        <Timeline
                            consignment={consignment}
                        />
                    </div>

                    {/* This is a hack */}
                    <div className="clearfix">
                        <hr className="clearfix" style={{marginTop: '25px'}}/>
                    </div>

                    {'COMPLETED' === consignment.status &&
                    <div className="clearfix">
                        <h3 className="section-header">Please rate our service</h3>
                        <ServiceRating
                            apiOrigin={apiOrigin}
                            trackingToken={stateTrackingToken}
                            consignment={consignment}
                        />
                        <CallToAction
                            cta={consignment.deliveryCompleteCallToAction}
                        />
                        <hr />
                    </div>}

                    {consignment.freight && consignment.freight.length > 0 &&
                        <div className="clearfix">
                            <>{stateFreightAsTable
                                ? <FreightTable freight={consignment.freight}/>
                                : <FreightList
                                    freight={consignment.freight}
                                    cumulativeTotals={consignment.cumulativeTotals}
                                    cumulativeTotalsByProgress={consignment.cumulativeTotalsByProgress}
                                    cumulativeTotalsConsignmentCount={consignment.cumulativeTotalsConsignmentCount}
                                />}
                                <hr />
                            </>
                        </div>
                    }

                    {consignment.requirements && consignment.requirements.length > 0 &&
                        <div className="clearfix">
                            <h3 className="section-header">Requirements</h3>
                            <ul>
                                {consignment.requirements.map((it, idx) => {
                                    return (<li key={idx}>{it.title}</li>)
                                })}
                            </ul>
                            <hr />
                        </div>
                    }

                    {consignment.images && consignment.images.length > 0 &&
                        <div className="clearfix">
                            <h3 className="section-header">Delivery Photos</h3>
                            <div className="row">
                                <div className="col-xs-12">
                                    <ConsignmentImageGallery
                                        apiOrigin={apiOrigin}
                                        trackingToken={stateTrackingToken}
                                        images={consignment.images}
                                    />
                                </div>
                            </div>
                            <hr />
                        </div>
                    }

                    {'CANCELLED' !== consignment.status &&
                        <div className="clearfix">
                            <div className="row middle-xs">
                                <div className="col-xs-2 col-md-1 center-xs">
                                    <IconSender />
                                </div>
                                <div className="col-xs-10 col-md-11 ">
                                    <p className="section-header">{consignment.sender.name}</p>
                                </div>
                            </div>
                            <div className="row middle-xs">
                                <div className="col-xs-2 col-md-1 center-xs">
                                    <IconReceiver />
                                </div>
                                <div className="col-xs-10 col-md-11">
                                    <ConsignmentWaypointAddress
                                        waypoint={consignment.receiver}
                                    />
                                </div>
                            </div>

                            { receiverAddress && receiverAddress.latitude && receiverAddress.longitude &&
                            <Map
                                receiverLatLng={{lat: receiverAddress.latitude, lng: receiverAddress.longitude}}
                                driverLatLng={driverLatLng}
                                driverBearing={driverBearing}
                            />
                            }
                        </div>
                    }

                    {consignment.driver &&
                        <div className="clearfix">
                            <div className="row">
                                <div className="col-xs-12 col-md-4">
                                    <h3 className="section-header">Driver & Vehicle Details</h3>
                                    <DriverAndVehicle
                                        apiOrigin={apiOrigin}
                                        trackingToken={stateTrackingToken}
                                        driver={consignment.driver}
                                        vehicle={consignment.vehicle}
                                        circular={stateCircularDriverPhoto}
                                    />
                                </div>
                                {'STARTED' === consignment.status && consignment.driver && consignment.driverMessagingEnabled &&
                                    <div className="col-xs">
                                        <DriverMessage
                                            apiOrigin={apiOrigin}
                                            trackingToken={stateTrackingToken}
                                            driverMessages={consignment.driverMessages}
                                            onMessageSent={(consignment: PublicApiConsignmentResponse) => setStateConsignment(consignment)}
                                        />
                                    </div>
                                }
                            </div>
                            <hr style={{marginTop: '20px'}}/>
                        </div>
                    }

                    <div className="footer">
                        <SubTruxBrand />
                    </div>

                </div>

            }
            </div>

        </div>
    );

}

export default App;
